import { FC, ReactNode } from 'react';
import { Stack, Typography } from '@mui/material';

import { PassengerBookingView } from '@mopla/data-models';
import { useTranslation } from '@mopla/i18';
import {
	checkIsBookingCancelled,
	checkIsLongDelayFreeCancellation,
	checkIsPassedBooking,
	checkIsSchoolCancellationBlocked,
	getItineraryTransportTypes,
	isLBTItinerary,
} from '@mopla/utils';

import {
	ArrowRightIcon,
	BottomSlider,
	ItineraryDateTimeInfo,
	MainWithFooterTemplate,
	MplButton,
	MplPaper,
	SchoolCancellationDeadlineMessage,
	TicketDetailsHeader,
	TripScheme,
} from '../../../..';
import { useBookingCancellation } from '../../../BookingCancellationFlow';

import { useMoreActionsContext } from './context';

interface IMoreActionsProps {
	booking: PassengerBookingView;
	additionalButtons?: ReactNode;
	open: boolean;
	passengerInfoBlock?: ReactNode;
}

export const MoreActions: FC<IMoreActionsProps> = ({
	open,
	booking,
	additionalButtons,
	passengerInfoBlock,
}) => {
	const { t } = useTranslation(['tickets', 'booking']);
	const { cancelBooking } = useBookingCancellation();
	const { closeMoreActions } = useMoreActionsContext();

	const isCancelledTrip = checkIsBookingCancelled(booking);
	const isPassedTrip = checkIsPassedBooking(booking);
	const isLBTRide = isLBTItinerary(booking.itinerary);
	const isLongDelayFreeCancellation = checkIsLongDelayFreeCancellation(
		booking.itinerary
	);

	const isSchoolCancellationBlocked = checkIsSchoolCancellationBlocked(booking);

	return (
		<BottomSlider
			open={open}
			withHeader
			title="More Actions"
			onBack={closeMoreActions}
			onClose={closeMoreActions}
			backgroundColor="neutral.40"
		>
			<MainWithFooterTemplate
				content={
					<MplPaper gap={2} sx={{ minHeight: '100%' }} backgroundOpacity={0}>
						<TicketDetailsHeader
							title={t('booking:booking_invoice.your_journey') + ':'}
							itineraryDateTime={
								<ItineraryDateTimeInfo
									route={booking.itinerary}
									showDurationInfo={false}
								/>
							}
							transport={getItineraryTransportTypes(booking.itinerary)}
						/>
						<TripScheme bookingItineraryLegs={booking.itinerary.legs} />
						<Stack gap={2}>
							<Typography variant="body2" fontWeight={600}>
								{t('tickets:text.passengers')}:
							</Typography>
							{passengerInfoBlock}
							{additionalButtons}
							{isSchoolCancellationBlocked ? (
								<SchoolCancellationDeadlineMessage dataTestid="moreActions-schoolCancellationDeadline-message" />
							) : (
								<MplButton
									fullWidth
									color="warningRevert"
									data-testid="moreActions-cancelBooking-button"
									onClick={() => {
										cancelBooking(booking);
									}}
									endIcon={<ArrowRightIcon />}
								>
									{isLBTRide || isCancelledTrip || isPassedTrip
										? t('tickets:button.remove_ride')
										: t('tickets:button.cancel_booking', {
												context: isLongDelayFreeCancellation && 'free',
										  })}
								</MplButton>
							)}
						</Stack>
					</MplPaper>
				}
			/>
		</BottomSlider>
	);
};
